/**
 * Header API — fetches header settings + mega menu from WordPress.
 */

import type { HeaderData } from '../types/header';
import { apiClient } from './client';

const FALLBACK: HeaderData = {
  settings: {
    logo: '',
    logoDark: '',
    logoAlt: 'Operisoft',
    logoWidthDesktop: 100,
    logoWidthMobile: 80,
    cta: { text: 'Get a Free Assessment', url: '/contact' },
    megaMenuCta: {
      icon: '',
      title: 'Get a Free Cloud Assessment',
      description: 'Evaluate your cloud readiness and get personalized recommendations.',
      buttonText: 'Get a Free Assessment',
      buttonUrl: '/contact',
    },
    badges: [
      { icon: '', text: 'AWS Advanced Consulting Partner' },
      { icon: '', text: '100+ Successful Cloud Migrations' },
      { icon: '', text: '24x7 Managed Cloud Support' },
      { icon: '', text: 'Certified AWS Experts' },
    ],
  },
  menu: [
    {
      id: 1, label: 'How We Help', url: '#', target: '_self', hasMegaMenu: true,
      children: [
        {
          id: 11, label: 'Move to Cloud', url: '/services/cloud', target: '_self',
          description: 'Migrate, modernize and manage on AWS', icon: '', image: '',
          children: [
            {
              id: 111, label: 'Plan & Assess', url: '#', target: '_self',
              description: '', icon: '', image: '',
              children: [
                { id: 1111, label: 'Cloud Readiness Assessment', url: '/contact?service=cloud-assessment', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1112, label: 'Cloud Strategy & Roadmap', url: '/contact?service=cloud-strategy', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1113, label: 'TCO & Business Case', url: '/contact?service=cloud-tco', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1114, label: 'Well-Architected Review', url: '/contact?service=cloud-review', target: '_self', description: '', icon: '', image: '', children: [] },
              ],
            },
            {
              id: 112, label: 'Migrate & Build', url: '#', target: '_self',
              description: '', icon: '', image: '',
              children: [
                { id: 1121, label: 'AWS Migration', url: '/contact?service=aws-migration', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1122, label: 'Landing Zone & Foundation', url: '/contact?service=cloud-landing-zone', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1123, label: 'Application Modernization', url: '/contact?service=cloud-modernization', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1124, label: 'Database Migration', url: '/contact?service=cloud-database', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1125, label: 'Backup & Disaster Recovery', url: '/contact?service=cloud-dr', target: '_self', description: '', icon: '', image: '', children: [] },
              ],
            },
            {
              id: 113, label: 'Optimize & Manage', url: '#', target: '_self',
              description: '', icon: '', image: '',
              children: [
                { id: 1131, label: 'Cloud Optimization (FinOps)', url: '/contact?service=cloud-finops', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1132, label: 'Performance Optimization', url: '/contact?service=cloud-performance', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1133, label: 'Managed Cloud Services', url: '/contact?service=cloud-managed', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1134, label: 'Automation & IaC', url: '/contact?service=cloud-iac', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1135, label: '24x7 Cloud Operations', url: '/contact?service=cloud-operations', target: '_self', description: '', icon: '', image: '', children: [] },
              ],
            },
            {
              id: 114, label: 'Resources', url: '#', target: '_self',
              description: '', icon: '', image: '',
              children: [
                { id: 1141, label: 'Best Practices', url: '/resources/best-practices', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1142, label: 'Frameworks & Guides', url: '/resources/frameworks', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1143, label: 'Tools & Accelerators', url: '/resources/tools', target: '_self', description: '', icon: '', image: '', children: [] },
                { id: 1144, label: 'Architectural Patterns', url: '/resources/patterns', target: '_self', description: '', icon: '', image: '', children: [] },
              ],
            },
          ],
        },
        { id: 12, label: 'Secure Your Business', url: '/services/security', target: '_self', description: 'Protect your data, apps and users', icon: '', image: '', children: [] },
        { id: 13, label: 'Build with AI', url: '/services/ai', target: '_self', description: 'Turn data into intelligence and innovation', icon: '', image: '', children: [] },
        { id: 14, label: 'Modernize Applications', url: '/services/modernize', target: '_self', description: 'Modern apps. Faster delivery. Better scale.', icon: '', image: '', children: [] },
        { id: 15, label: 'Reduce IT Costs', url: '/services/cost', target: '_self', description: 'Optimize spend and maximize value', icon: '', image: '', children: [] },
        { id: 16, label: 'Stay Compliant', url: '/services/compliance', target: '_self', description: 'Meet standards and reduce risk', icon: '', image: '', children: [] },
        { id: 17, label: 'Improve IT Operations', url: '/services/operations', target: '_self', description: 'Automate, observe and improve reliability', icon: '', image: '', children: [] },
        { id: 18, label: 'Managed IT Services', url: '/services/managed', target: '_self', description: '24x7 management of your IT environment', icon: '', image: '', children: [] },
      ],
    },
    { id: 2, label: 'Industries', url: '/industries', target: '_self', hasMegaMenu: false, children: [] },
    { id: 3, label: 'Solutions', url: '/solutions', target: '_self', hasMegaMenu: false, children: [] },
    { id: 4, label: 'Insights', url: '/blog', target: '_self', hasMegaMenu: false, children: [] },
    { id: 5, label: 'Partners', url: '/partners', target: '_self', hasMegaMenu: false, children: [] },
    { id: 6, label: 'About', url: '/about', target: '_self', hasMegaMenu: false, children: [] },
    { id: 7, label: 'Contact', url: '/contact', target: '_self', hasMegaMenu: false, children: [] },
  ],
};

export async function fetchHeaderData(): Promise<HeaderData> {
  try {
    const response = await apiClient.get<HeaderData>('/wp-json/otpl/v1/header');
    return response.data;
  } catch {
    return FALLBACK;
  }
}
